import React, { useState } from "react";
import {
  Box,
  Drawer,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  AppBar,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

const drawerWidth = 256;

const menuItems = [
  {
    text: "Lead Generation",
    icon: "/IconLG.svg",
    path: "/candidate-sourcing",
  },
  {
    text: "Lead Board",
    icon: "/IconLB.svg",
    path: "/lead-board",
  },
  {
    text: "Voice Agent",
    icon: "/IconVA.svg",
    path: "/voice-agent",
  },
  {
    text: "Lead History",
    icon: "/IconLH.svg",
    path: "/lead-history",
  },
];

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActive = (path) => {
    // If we're on company-results page, check the 'from' param to determine active state
    if (location.pathname === "/company-results") {
      const fromPage = searchParams.get("from") || "candidate-sourcing";
      if (fromPage === "lead-history" && path === "/lead-history") {
        return true;
      }
      if (fromPage === "candidate-sourcing" && path === "/candidate-sourcing") {
        return true;
      }
      return false;
    }

    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    // Clear localStorage
    localStorage.clear();
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Navigate to login page
    navigate("/login");
    
    // Close mobile drawer if open
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFFFFF",
        borderRight: "1px solid #E5E7EB",
        overflowX: "hidden",
        overflowY: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Logo Container */}
      <Box
        sx={{
          width: "100%",
          height: { xs: "64px", md: "89px" },
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "flex-start" },
          paddingLeft: { xs: "16px", md: "29px" },
          paddingRight: { xs: "16px", md: "29px" },
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/SalesAgent.svg"
          alt="Logo"
          sx={{
            height: { xs: "28px", md: "37px" },
            width: "auto",
            maxWidth: "calc(100% - 32px)",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* Navigation Menu */}
      <Box
        sx={{
          width: "100%",
          flex: 1,
          paddingTop: { xs: "12px", md: "16px" },
          paddingLeft: { xs: "12px", md: "16px" },
          paddingRight: { xs: "12px", md: "16px" },
          display: "flex",
          flexDirection: "column",
          gap: { xs: "6px", md: "8px" },
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D1D5DB",
            borderRadius: "2px",
            "&:hover": {
              backgroundColor: "#9CA3AF",
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <Box
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              width: "100%",
              minHeight: { xs: "40px", md: "44px" },
              height: { xs: "40px", md: "44px" },
              paddingLeft: { xs: "12px", md: "16px" },
              paddingRight: { xs: "12px", md: "16px" },
              display: "flex",
              alignItems: "center",
              gap: { xs: "10px", md: "12px" },
              borderRadius: "10px",
              backgroundColor: isActive(item.path) ? "#EFF6FF" : "transparent",
              cursor: "pointer",
              boxSizing: "border-box",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: isActive(item.path) ? "#EFF6FF" : "#F5F5F5",
              },
              "&:active": {
                backgroundColor: isActive(item.path) ? "#DBEAFE" : "#E5E7EB",
              },
            }}
          >
            <Box
              sx={{
                width: { xs: "18px", md: "20px" },
                height: { xs: "18px", md: "20px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={item.icon}
                alt={item.text}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: isActive(item.path)
                    ? "brightness(0) saturate(100%) invert(40%) sepia(95%) saturate(2476%) hue-rotate(208deg) brightness(98%) contrast(101%)"
                    : "brightness(0) saturate(100%) invert(25%) sepia(8%) saturate(1015%) hue-rotate(182deg) brightness(94%) contrast(88%)",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "Arial, sans-serif",
                fontWeight: isActive(item.path) ? 500 : 400,
                fontSize: { xs: "13px", md: "14px" },
                lineHeight: "20px",
                letterSpacing: "0px",
                color: isActive(item.path) ? "#155DFC" : "#364153",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Logout Button */}
      <Box
        sx={{
          width: "100%",
          padding: { xs: "12px", md: "16px" },
          borderTop: "1px solid #E5E7EB",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <Box
          onClick={handleLogout}
          sx={{
            width: "100%",
            minHeight: { xs: "40px", md: "44px" },
            height: { xs: "40px", md: "44px" },
            paddingLeft: { xs: "12px", md: "16px" },
            paddingRight: { xs: "12px", md: "16px" },
            display: "flex",
            alignItems: "center",
            gap: { xs: "10px", md: "12px" },
            borderRadius: "10px",
            backgroundColor: "transparent",
            cursor: "pointer",
            boxSizing: "border-box",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: "#F5F5F5",
            },
            "&:active": {
              backgroundColor: "#E5E7EB",
            },
          }}
        >
          <LogoutIcon
            sx={{
              width: { xs: "18px", md: "20px" },
              height: { xs: "18px", md: "20px" },
              color: "#364153",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontFamily: "Arial, sans-serif",
              fontWeight: 400,
              fontSize: { xs: "13px", md: "14px" },
              lineHeight: "20px",
              letterSpacing: "0px",
              color: "#364153",
              whiteSpace: "nowrap",
            }}
          >
            Logout
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        maxHeight: "100vh",
      }}
    >
      {/* Mobile App Bar */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: "block", md: "none" },
          width: "100%",
          backgroundColor: "#FFFFFF",
          color: "text.primary",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: "56px", px: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon sx={{ color: "#364153" }} />
          </IconButton>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
            <img
              src="/SalesAgent.svg"
              alt="Sales Agent"
              style={{ height: "32px", width: "auto" }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          zIndex: theme.zIndex.drawer,
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#FFFFFF",
              borderRight: "1px solid #E5E7EB",
              overflowX: "hidden !important",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#FFFFFF",
              borderRight: "1px solid #E5E7EB",
              position: "fixed",
              height: "100vh",
              boxShadow: "none",
              overflowX: "hidden !important",
              overflowY: "hidden !important",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: "#FFFFFF",
          height: { 
            xs: "calc(100vh - 56px)", 
            md: "100vh" 
          },
          maxHeight: { 
            xs: "calc(100vh - 56px)", 
            md: "100vh" 
          },
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          maxWidth: { md: `calc(100vw - ${drawerWidth}px)` },
          mt: { xs: "56px", md: 0 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: 0,
            maxWidth: "100%",
            width: "100%",
            boxSizing: "border-box",
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
            height: "100%",
            minHeight: 0,
            WebkitOverflowScrolling: "touch",
            /* iOS safe area support for better bottom spacing */
            paddingBottom: { 
              xs: "calc(60px + env(safe-area-inset-bottom, 0px))", 
              sm: "calc(40px + env(safe-area-inset-bottom, 0px))", 
              md: "0px" 
            },
            marginBottom: { xs: "0px", md: "0px" },
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F3F4F6",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#9CA3AF",
              },
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
